// Carrier curve — Detection Engine System 1 (spec §2.1).
//
// Таблица «конфигурация основного паттерна → какая её часть становится красной
// линией». НЕ формула — выбор части по семейству main pattern:
//   together  → среднее членов группы (дробная несущая)
//   singleton → сама выломившаяся линия (НЕ среднее — оно спрятало бы выброс)
//   split/opp → доминирующая линия; group-vs-group → среднее доминирующего БЛОКА
//   scatter   → силуэт = mean активных слоёв per stage
//
// Возвращает { c, fractional, members, paleLines, eyebrow }.

import { LAYERS, layerIndex, DISPLAY_NAME } from './layers.js';
import { series, seriesTouched, othersMeanAt, mean, isTouchedAt } from './input.js';

/**
 * @param main  выбранный основной паттерн (detection / scatter-sentinel)
 * @param stages нормализованные этапы
 * @param active активные слои
 */
export function carrierCurve(main, stages, active) {
  // Несущая эмитит `null` на этапе, где НИ ОДИН её член не тронут (spec §2.1, s443.d5
  // Фаза 2): дырка, не 0. Частично-тронутый этап → среднее по тронутым членам. Тип
  // number|null[] (контракт s5-маски). На all-touched === mean по всем членам (byte-parity).
  const meanOf = (layers) =>
    stages.map((_, i) => {
      const tm = layers.filter((L) => isTouchedAt(stages[i], L));
      return tm.length ? mean(tm.map((L) => stages[i].values[L])) : null;
    });

  switch (main.family) {
    case 'field': {
      // field-level-recovery → среднее V-поля (дробная несущая).
      const members = main.layers.slice();
      return finalize(meanOf(members), true, members, members, eyebrowField(members));
    }

    case 'together': {
      // parallel-whole / triplet-as-one → среднее членов (дробная).
      const members = main.layers.slice();
      return finalize(meanOf(members), members.length > 1, members, members, eyebrowMembers(members));
    }

    case 'singleton': {
      // сама линия (целочисленная). Бледные = поле (остальные активные). seriesTouched:
      // нетронутый-на-этапе → null (несущая-дырка), не 0 (§2.1, s443.d5 Ф2).
      const L = main.layers[0];
      const pale = active.filter((o) => o !== L);
      return finalize(seriesTouched(stages, L), false, [L], pale, eyebrowMembers([L]));
    }

    case 'opposition': {
      if (main.type === 'group-vs-group') {
        // среднее доминирующего БЛОКА (дробная).
        const block = main.dominantBlock.slice();
        const pale = main.layers.slice();
        return finalize(meanOf(block), block.length > 1, block, pale, eyebrowOpposition(block, main.layers));
      }
      // пара divergence/late-conv/anti-corr → доминирующая линия (целочисленная).
      const dom = dominantLine(stages, active, main.layers);
      const other = main.layers.find((L) => L !== dom);
      return finalize(seriesTouched(stages, dom), false, [dom], main.layers.slice(), eyebrowOpposition([dom], main.layers, other));
    }

    case 'scatter':
    default: {
      // силуэт = mean активных слоёв (= mean L1..L7 при всех touched).
      const members = active.length ? active.slice() : LAYERS.slice();
      return finalize(meanOf(members), true, members, members, { text: 'no single carrier', members: [] });
    }
  }
}

function finalize(c, fractional, members, paleLines, eyebrow) {
  return { c, fractional, members, paleLines, eyebrow };
}

/**
 * Доминирующая линия (§2.1): max amplitude среди членов; тай-брейк
 * (1) max |net|; (2) max |L(N)−mean(field@N)|; (3) фикс. порядок L1..L7.
 */
export function dominantLine(stages, active, members) {
  const N = stages.length;
  const score = members.map((L) => {
    const v = series(stages, L);
    return {
      L,
      amp: Math.max(...v) - Math.min(...v),
      net: Math.abs(v[N - 1] - v[0]),
      currentGap: Math.abs(v[N - 1] - othersMeanAt(stages, active, L, N - 1)),
      idx: layerIndex(L),
    };
  });
  score.sort(
    (a, b) =>
      b.amp - a.amp ||
      b.net - a.net ||
      b.currentGap - a.currentGap ||
      a.idx - b.idx,
  );
  return score[0].L;
}

// ── eyebrow (CARRIERS · …) members per §2.9 ─────────────────────────────────

function eyebrowMembers(members) {
  return { text: members.map((L) => DISPLAY_NAME[L]).join(' + '), members: members.slice() };
}
function eyebrowField(members) {
  return { text: 'the field', members: members.slice() };
}
function eyebrowOpposition(dominant, all, other) {
  const dom = dominant.map((L) => DISPLAY_NAME[L]).join(' + ');
  const vs = other ? DISPLAY_NAME[other] : 'the field';
  return { text: `${dom} vs ${vs}`, members: all.slice() };
}
